import { useRef, useState } from "react";
import { Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { deleteImage, uploadImage } from "@/utils/image/upload";

interface ImageUploadProps {
  bucket: "product-images" | "genie-images";
  value?: string;
  onChange: (url: string | undefined) => void;
  onBeforeRemove?: (url: string) => boolean;
  label?: string;
  className?: string;
}

export const ImageUpload = ({ bucket, value, onChange, onBeforeRemove, label = "Upload Image", className = "" }: ImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    const result = await uploadImage(file, bucket);

    if (result.error) {
      setError(result.error);
    } else {
      onChange(result.url);
    }

    setIsUploading(false);
  };

  const handleRemove = async () => {
    if (!value) return;

    setIsUploading(true);

    // Check if removal is allowed (e.g., don't delete team branding logo outside AccountBrandingPage)
    const shouldDeleteFromStorage = !onBeforeRemove || onBeforeRemove(value);

    if (shouldDeleteFromStorage) {
      const success = await deleteImage(value, bucket);

      if (!success) {
        setError("Failed to remove image");
        setIsUploading(false);
        return;
      }
    }

    onChange(undefined);
    setIsUploading(false);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {value ? (
        <div className="relative bg-gray-50 rounded-lg border border-gray-200 h-48 flex items-center justify-center">
          <img src={value} alt="Uploaded" className="max-w-full max-h-full object-contain rounded-lg" />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={handleRemove}
            disabled={isUploading}
            className="absolute top-2 right-2"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors h-48 flex flex-col justify-center"
        >
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">{isUploading ? "Uploading..." : "Click to upload image"}</p>
          <p className="text-xs text-gray-500 mt-1">JPEG, PNG, WebP, GIF up to 5MB</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
